/**
 * Guardrail for replacing the audio file on a Background Music track
 * (Monday 12735073581).
 *
 * Replacing a track's file used to change nothing users heard: the CMS copies
 * the track's URL onto each session at selection time, and nothing propagated
 * the swap. It failed silently — the old object still returns 200, so nothing
 * 404s and nothing logs, and this screen showed the new file as attached.
 * Users heard the wrong mix on the Core 5 sessions for five months.
 *
 * Both APIs now resolve a LOCKED session's music to the track's current file,
 * so a swap does propagate. That makes the reverse mistake possible: replacing
 * a file now silently changes every locked session at once. Hence this
 * confirmation, which states exactly what will and will not change.
 *
 * The counts matter because the two groups behave differently:
 *   - locked sessions follow this track and WILL change
 *   - unlocked sessions keep their own music bed and will NOT change
 *   - quick taps are matched by URL, not id, so they keep the old file AND
 *     drop off this track's usage list
 */

const pluralize = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const isLocked = (session) => Boolean(session && session.is_music_locked);

/**
 * Split a track's related content into the groups a replacement affects.
 *
 * @param {{sessions?: Array<object>, quick_taps?: Array<object>}} relatedContent
 */
export function replacementImpact(relatedContent) {
  const sessions = Array.isArray(relatedContent && relatedContent.sessions)
    ? relatedContent.sessions
    : [];
  const quickTaps = Array.isArray(relatedContent && relatedContent.quick_taps)
    ? relatedContent.quick_taps
    : [];

  return {
    locked: sessions.filter(isLocked),
    unlocked: sessions.filter((session) => !isLocked(session)),
    quickTaps,
  };
}

/**
 * Whether swapping the file warrants a confirmation.
 *
 * Only on edit, only when the URL actually changed, and only when something
 * is attached to the track — a brand-new track or an unchanged file has no
 * consequence to warn about, and a dialog nobody needs is a dialog everybody
 * learns to dismiss.
 *
 * @param {{isEdit: boolean, originalUrl: string, nextUrl: string, relatedContent: object}} params
 */
export function shouldConfirmReplacement({ isEdit, originalUrl, nextUrl, relatedContent }) {
  if (!isEdit) return false;
  if (!originalUrl || !nextUrl) return false;
  if (originalUrl === nextUrl) return false;

  const { locked, quickTaps } = replacementImpact(relatedContent);

  return locked.length > 0 || quickTaps.length > 0;
}

/**
 * Body of the confirmation, as HTML for SweetAlert.
 *
 * Leads with what changes, then what does not — an editor who replaces a file
 * expecting everything to update needs the "will not change" half most.
 *
 * @param {object} relatedContent
 * @returns {string}
 */
export function replacementWarningHtml(relatedContent) {
  const { locked, unlocked, quickTaps } = replacementImpact(relatedContent);
  const lines = [];

  if (locked.length > 0) {
    const names = locked.slice(0, 5).map((session) => session.title).filter(Boolean);
    const more = locked.length - names.length;

    lines.push(
      `<p><strong>${pluralize(locked.length, 'session')} will start playing the new file.</strong></p>`
    );
    if (names.length > 0) {
      lines.push(
        `<ul style="text-align:left;margin:0 0 1em 1.5em">${names
          .map((name) => `<li>${escapeHtml(name)}</li>`)
          .join('')}${more > 0 ? `<li>and ${pluralize(more, 'other')}</li>` : ''}</ul>`
      );
    }
  }

  if (unlocked.length > 0) {
    lines.push(
      `<p>${pluralize(unlocked.length, 'session')} using this track ` +
        `<strong>will not change</strong> — they keep their own music bed.</p>`
    );
  }

  if (quickTaps.length > 0) {
    lines.push(
      `<p><strong>${pluralize(quickTaps.length, 'quick tap')} will keep the old file.</strong> ` +
        'Quick taps are matched by URL, so they stay on the previous audio and drop off this ' +
        "track's usage list. Update them by hand.</p>"
    );
  }

  return lines.join('');
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
